import React, { useState, useCallback, useMemo, Fragment } from 'react';
import { PieChart as RechartsPieChart, Pie, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import useWindowSize from 'hooks/useDynamicScreenSize';
import { useReport } from '../ReportContext';
import { Legend } from '../Layout';
import { useSeries, useTransformData, useChartColors } from '../hooks';
import { useChartStyles, useTooltipHandlers, useDrilldownHandler, ChartTooltip } from './utils';

const RADIAN = Math.PI / 180;

export const PieChart = ({ data, config }) => {
  const { classes } = useChartStyles();
  const windowSize = useWindowSize();
  const [mouseOverOuter, setMouseOverOuter] = useState(null); // Outer ring/Single pie hover index
  const [mouseOverInner, setMouseOverInner] = useState(null); // Inner ring hover { outerIndex, innerIndex }
  const { isDrilldownEnabled } = useReport();
  const handleSliceClick = useDrilldownHandler(config);
  const chartColors = useChartColors(); // Get color functions

  // Use base data transformation hook
  const transformedData = useTransformData(data);
  
  // Get series config from the configuration
  const { series, showLegend } = useSeries(config);

  // Determine if we should render two rings - ONLY based on series length
  // If we have more than one series, it's a multi-ring chart
  const hasTwoSeries = series.length > 1;
  
  // Special case override: if config explicitly says to not render as two rings, respect that
  const useMultiRing = hasTwoSeries && config.renderAsTwoRings !== false;

  // --- Data Processing for Rings --- 

  const outerRingData = useMemo(() => {
    if (!transformedData) return [];

    // Only process as multi-ring data if we have multiple series
    if (useMultiRing) {
      // Use the first series as the category axis for the outer ring
      const outerSeries = series[0];
      
      return transformedData.map(row => {
        // Sum numeric values for the outer ring total
        const totalCount = Object.keys(row)
          .filter(key => typeof row[key] === 'number')
          .reduce((sum, key) => sum + row[key], 0);
        
        // Find the category field - look for a non-numeric field that's not 'id'
        const categoryField = Object.keys(row).find(key => 
          typeof row[key] === 'string' && key !== 'id'
        );
        
        return {
          name: categoryField ? row[categoryField] : 'Unknown', // Use the found category field
          count: totalCount,
          originalRow: row // Keep reference to the original pivoted row
        };
      });
    }

    // Single pie chart case
    const outerSeries = series[0];
    
    return transformedData.map(item => {
      // Find the category field - look for a non-numeric field that's not 'id'
      const categoryField = Object.keys(item).find(key => 
        typeof item[key] === 'string' && key !== 'id'
      );
      
      return {
        name: categoryField ? item[categoryField] : 'Unknown', // Use the found category field
        count: item.count || (typeof item.value === 'number' ? item.value : 0),
        seriesConfig: outerSeries,
        ...item // Include original item data
      };
    });
    
  }, [transformedData, useMultiRing, series]);

  // Calculate the full list of potential inner slices (before hover filtering)
  const allInnerSlices = useMemo(() => {
    if (!useMultiRing) return []; // Only relevant for multi-ring
    if (!transformedData || !series) return [];
    
    const slices = [];
    // Use outerRingData directly here as it's calculated before
    outerRingData.forEach((outerSegment, outerIndex) => {
      const row = outerSegment.originalRow;
      series.forEach((seriesItem, seriesIndex) => {
        const dataKey = seriesItem.key;
        const count = row[dataKey];
        if (typeof count === 'number' && count > 0) {
          slices.push({
            name: seriesItem.label, 
            count: count, 
            parentName: outerSegment.name, // Generic parent segment name
            seriesConfig: seriesItem, 
            outerIndex: outerIndex, 
            originalIndex: seriesIndex,
            originalItem: row
          });
        }
      });
    });
    return slices;
  }, [useMultiRing, transformedData, series, outerRingData]);

  // --- Determine Rendering Mode --- 
  const renderAsTwoRings = useMemo(() => 
    useMultiRing && outerRingData.length > 1
  , [useMultiRing, outerRingData]);

  // Calculate the inner ring data to be rendered (potentially filtered by hover)
  const innerRingData = useMemo(() => {
    // Return full list if not hovered or not in two-ring mode
    if (!renderAsTwoRings || mouseOverOuter === null) {
      return allInnerSlices;
    }
    // Filter based on outer ring hover when in two-ring mode
    return allInnerSlices.filter(slice => slice.outerIndex === mouseOverOuter);
  }, [allInnerSlices, renderAsTwoRings, mouseOverOuter]);

  // --- Event Handlers --- 

  const handleOuterSliceMouseEnter = useCallback((entry, index) => {
    setMouseOverOuter(index);
    if (renderAsTwoRings) {
        setMouseOverInner(null); // Clear inner if hovering outer in two-ring mode
    }
  }, [renderAsTwoRings]);

  const handleOuterSliceMouseLeave = useCallback(() => {
    setMouseOverOuter(null);
  }, []);

  const handleInnerSliceMouseEnter = useCallback((entry, index) => {
    // Set hover state for inner ring (relevant only in two-ring mode)
    setMouseOverInner({ outerIndex: entry.outerIndex, innerIndex: index });
  }, []);

  const handleInnerSliceMouseLeave = useCallback(() => {
    setMouseOverInner(null);
  }, []);

  // Combined Click Handler
  const handleCombinedClick = useCallback((entry, index) => {
    // Only execute click handler if drilldown is enabled
    if (!isDrilldownEnabled()) return;
    
    if (renderAsTwoRings) {
      // Click is on inner ring slice
      handleSliceClick(entry, index, { 
        ...entry.seriesConfig,  // Spread the entire series config
        isInnerRing: true 
      });
    } else {
      // Click is on single pie slice
      handleSliceClick(entry, index, entry.seriesConfig);  // Pass entire series config
    }
  }, [config, renderAsTwoRings, handleSliceClick, isDrilldownEnabled]);

  // --- Label Rendering (Text Only) --- 
  const renderOuterRingLabel = (props) => {
    const { cx, cy, midAngle, outerRadius, payload } = props;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const textColor = '#333333';
    return (
      <text x={x} y={y} fill={textColor} textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central" fontSize={17.3} fontWeight="bold" >
        {`${payload.name} (${payload.count})`}
      </text>
    );
  };

  const renderInnerPieLabel = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, index, payload } = props;
    
    // Calculate total for percentage based on slices in the *same* outer ring segment
    const relevantOuterIndex = payload.outerIndex;
    const totalForOuterSegment = allInnerSlices
      .filter(slice => slice.outerIndex === relevantOuterIndex)
      .reduce((sum, entry) => sum + entry.count, 0);
      
    const percent = totalForOuterSegment > 0 ? Math.round((payload.count / totalForOuterSegment) * 100) : 0;

    // Outer label position
    const outerLabelRadius = outerRadius + 15;
    const outerX = cx + outerLabelRadius * Math.cos(-midAngle * RADIAN);
    const outerY = cy + outerLabelRadius * Math.sin(-midAngle * RADIAN);
    const textColor = '#333333';
    const anchor = outerX > cx ? 'start' : 'end';

    const outerLabelElement = (
      <text 
        x={outerX} 
        y={outerY} 
        fill={textColor} 
        textAnchor={anchor} 
        dominantBaseline="central"
        fontSize={15.6} 
        fontWeight="500" 
      >
        {`${payload.name} (${payload.count})`}
      </text>
    );

    // Only show percentage label if the slice is large enough
    if (percent >= 3) {
      // Position for the percentage label (inside the slice)
      const insideRadius = (innerRadius + outerRadius) * 0.6;
      const insideX = cx + insideRadius * Math.cos(-midAngle * RADIAN);
      const insideY = cy + insideRadius * Math.sin(-midAngle * RADIAN);

      return (
        <Fragment key={`inner-label-${index}`}> {/* Add key */}
          {/* Percentage label inside the slice */}
          <text
            x={insideX}
            y={insideY}
            fill="#FFFFFF"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={18} // Make it slightly larger like the other inner percentage
            fontWeight="bold"
          >
            {`${percent}%`}
          </text>
          
          {/* Original label outside the slice */}
          {outerLabelElement}
        </Fragment>
      );
    } else {
      // For smaller slices, just show the outer label
      return <Fragment key={`inner-label-${index}`}>{outerLabelElement}</Fragment>;
    }
  };

  const renderSingleNonPivotedLabel = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, index, payload } = props;

    // Calculate total for percentage (only for this non-pivoted single series case)
    const total = outerRingData.reduce((sum, entry) => sum + entry.count, 0);
    const percent = total > 0 ? Math.round((payload.count / total) * 100) : 0;

    // Outer label position
    const outerLabelRadius = outerRadius + 30;
    const outerX = cx + outerLabelRadius * Math.cos(-midAngle * RADIAN);
    const outerY = cy + outerLabelRadius * Math.sin(-midAngle * RADIAN);

    // Use series color for the outer label
    const seriesColor = payload.seriesConfig?.getColorByIndex?.(index) || payload.seriesConfig?.defaultColor || '#333333';
    const outerAnchor = outerX > cx ? 'start' : 'end';

    const outerLabelElement = (
      <text
        x={outerX}
        y={outerY}
        fill={seriesColor} // Use series color here
        textAnchor={outerAnchor}
        dominantBaseline="central"
        fontSize={15.6}
        fontWeight="500"
      >
        {`${payload.name} (${payload.count})`}
      </text>
    );

    // Only show percentage label if the slice is large enough
    if (percent >= 3) {
      // Position for the percentage label (inside the slice)
      const insideRadius = (innerRadius + outerRadius) * 0.6;
      const insideX = cx + insideRadius * Math.cos(-midAngle * RADIAN);
      const insideY = cy + insideRadius * Math.sin(-midAngle * RADIAN);

      return (
        <Fragment key={`single-label-${index}`}> {/* Add key */}
          {/* Percentage label inside the slice */}
          <text
            x={insideX}
            y={insideY}
            fill="#FFFFFF"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={18}
            fontWeight="bold"
          >
            {`${percent}%`}
          </text>
          
          {/* Original label outside the slice */}
          {outerLabelElement}
        </Fragment>
      );
    } else {
      // For smaller slices, just show the outer label
      // We need the key here too for React list rendering
      return <Fragment key={`single-label-${index}`}>{outerLabelElement}</Fragment>;
    }
  };

  // --- Label Line Rendering Functions (Restored) --- 
  const handleOuterRingLabelLine = (props) => {
    const { points, index } = props;
    const firstInnerSlice = allInnerSlices.find(inner => inner.outerIndex === index);
    const color = firstInnerSlice?.seriesConfig?.defaultColor || chartColors.getColorByIndex(index, 'neutral'); // Keep color logic from before
    // Use dark color for line to match label
    const lineColor = '#333333';
    return <line x1={points[0].x} y1={points[0].y} x2={points[1].x} y2={points[1].y} fill={lineColor} style={{ stroke: lineColor, strokeWidth: 1.8 }} />;
  };

  const handleInnerPieLabelLine = (props) => {
    const { points } = props; // Payload not needed here
    // Use dark color for line to match label
    const color = '#333333'; 
    return <line x1={points[0].x} y1={points[0].y} x2={points[1].x} y2={points[1].y} fill={color} style={{ stroke: color, strokeWidth: 1.8 }} />;
  };

  const handleSingleNonPivotedLabelLine = (props) => {
    const { points } = props; // Payload not needed here
    // Use dark color for line to match label
    const color = '#333333';
    return <line x1={points[0].x} y1={points[0].y} x2={points[1].x} y2={points[1].y} fill={color} style={{ stroke: color, strokeWidth: 1.8 }} />;
  };

  // --- Chart Dimensions --- 
  const viewportMin = Math.min(windowSize.width, windowSize.height);
  let mainRadius = viewportMin * 0.25;
  mainRadius = Math.max(mainRadius, 180); 
  mainRadius = Math.min(mainRadius, 300); 
  if (windowSize.width < 1200) mainRadius = Math.min(mainRadius, viewportMin * 0.22);
  if (windowSize.width < 775) mainRadius = Math.min(mainRadius, viewportMin * 0.20);
  if (windowSize.width < 600) mainRadius = Math.min(mainRadius, viewportMin * 0.18);

  // Define radii for two rings
  // Make outer ring thinner (approx 15% of mainRadius)
  const innerRingRadius = mainRadius * 0.8; // Inner pie takes up 80%
  const outerRingInnerRad = mainRadius * 0.85; // Outer ring starts after a gap
  const outerRingOuterRadius = mainRadius; // Outer ring goes to the edge
  
  // --- Determine Props for the main/single Pie --- 
  const singlePieProps = useMemo(() => {
    if (renderAsTwoRings) return {}; // Props not used if rendering two pies separately
    
    return {
      data: outerRingData,
      label: renderSingleNonPivotedLabel,
      labelLine: handleSingleNonPivotedLabelLine, // Restored
      paddingAngle: 2,
      dataKey: "count",
      nameKey: "name",
      cx: "50%",
      cy: "50%",
      innerRadius: 0,
      outerRadius: outerRingOuterRadius,
      onClick: isDrilldownEnabled() ? handleCombinedClick : undefined,
      onMouseEnter: handleOuterSliceMouseEnter, // Use outer handler for single pie hover index
      onMouseLeave: handleOuterSliceMouseLeave,
      cursor: isDrilldownEnabled() ? 'pointer' : 'default',
      className: isDrilldownEnabled() ? classes.clickableSlice : '',
      isAnimationActive: false
    };
  }, [renderAsTwoRings, outerRingData, outerRingOuterRadius, handleCombinedClick, handleOuterSliceMouseEnter, handleOuterSliceMouseLeave, isDrilldownEnabled, classes]);

  // --- Render --- 
  return (
    <>
      <div className={classes.pieStyles} style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <div style={{ height: "100%", flexGrow: 1, position: 'relative', zIndex: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart margin={{ top: 50, right: 100, bottom: 50, left: 100 }}>
              {renderAsTwoRings ? (
                <>
                  {/* Outer Ring FIRST (for stacking order) */} 
                  <Pie
                    data={outerRingData}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={outerRingOuterRadius} 
                    innerRadius={outerRingInnerRad}
                    label={renderOuterRingLabel}
                    labelLine={handleOuterRingLabelLine} // Restored
                    onClick={() => {}} 
                    cursor={'default'}
                    onMouseEnter={handleOuterSliceMouseEnter}
                    onMouseLeave={handleOuterSliceMouseLeave}
                    isAnimationActive={false}
                    paddingAngle={1}
                  >
                    {outerRingData?.map((entry, index) => {
                      const firstInnerSlice = allInnerSlices.find(inner => inner.parentName === entry.name);
                      const color = firstInnerSlice?.seriesConfig?.defaultColor || chartColors.getColorByIndex(index, 'neutral');
                      const isOuterHighlighted = index === mouseOverOuter;
                      return (
                        <Cell
                          key={`outer-cell-${entry.name}-${index}`}
                          stroke={isOuterHighlighted ? '#ffffff' : '#fff'}
                          strokeWidth={isOuterHighlighted ? 3 : 1.8}
                          fill={isOuterHighlighted ? `${color}CC` : color}
                        />
                      );
                    })}
                  </Pie>

                  {/* Inner Pie SECOND (renders later, potentially on top) */} 
                  <Pie
                    data={innerRingData}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={innerRingRadius} 
                    innerRadius={0}
                    label={renderInnerPieLabel} 
                    labelLine={handleInnerPieLabelLine} // Restored
                    onClick={isDrilldownEnabled() ? handleCombinedClick : undefined}
                    cursor={isDrilldownEnabled() ? 'pointer' : 'default'}
                    onMouseEnter={handleInnerSliceMouseEnter}
                    onMouseLeave={handleInnerSliceMouseLeave}
                    isAnimationActive={false}
                    className={isDrilldownEnabled() ? classes.clickableSlice : ''}
                    paddingAngle={1}
                  >
                    {innerRingData?.map((entry, index) => {
                      const color = entry.seriesConfig?.defaultColor || '#ccc';
                      const isHighlighted = mouseOverInner?.outerIndex === entry.outerIndex && 
                                            mouseOverInner?.innerIndex === index;
                      return (
                        <Cell
                          key={`inner-cell-${entry.parentName}-${entry.name}-${index}`}
                          stroke={isHighlighted ? '#ffffff' : '#fff'}
                          strokeWidth={isHighlighted ? 3 : 1.8}
                          fill={isHighlighted ? `${color}CC` : color}
                        />
                      );
                    })}
                  </Pie>
                  
                </>
              ) : (
                /* Single Pie (when not two rings) */
                <Pie {...singlePieProps}>
                  {singlePieProps.data?.map((entry, index) => {
                    // Use color from series config
                    const color = entry.seriesConfig?.getColorByIndex?.(index) || 
                                  entry.seriesConfig?.defaultColor || 
                                  chartColors.getColorByIndex(index, 'neutral');
                    
                    const isHighlighted = index === mouseOverOuter; // Use outer hover state for single pie highlight
                    
                    return (
                      <Cell
                        key={`single-cell-${entry.name}-${index}`}
                        stroke={isHighlighted ? '#ffffff' : '#fff'}
                        strokeWidth={isHighlighted ? 3 : 1.8}
                        fill={isHighlighted ? `${color}CC` : color}
                      />
                    );
                  })}
                </Pie>
              )}
              
              <Tooltip 
                content={<ChartTooltip legendItems={series} chartType="pie" />} 
                wrapperStyle={{ zIndex: 1000 }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
        {showLegend() && <Legend items={series} chartType="pie" position="right" />} 
      </div>
    </>
  );
}; 
